/**
 * RogerDoc Store Slice
 *
 * Manages state for RogerDoc dashboard functionality including:
 * - User management
 * - Usage statistics
 * - Overview metrics
 */

import { StateCreator } from 'zustand';
import {
  getAllUsers,
  getActiveUsers,
  getVisitStats,
  getTimeSavings,
  getTopSavingsClinicians,
  getUsageStats,
  addUser,
  resetUserPassword,
  deactivateUser,
  type RogerDocUser,
  type RogerDocActiveUser,
  type RogerDocVisitStats,
  type RogerDocTimeSavings,
  type RogerDocTopSavingsClinic,
  type RogerDocUsageByClinic,
  type AddUserData,
  type AddUserResponse,
  type AddUserResponseWithData,
} from '../../services/rogerDocApi';

export interface RogerDocSlice {
  // State
  users: RogerDocUser[];
  activeUsers: RogerDocActiveUser[];
  visitStats: RogerDocVisitStats | null;
  timeSavings: RogerDocTimeSavings | null;
  topSavingsClinicians: RogerDocTopSavingsClinic[];
  usageStats: RogerDocUsageByClinic[];
  usageCalculatedAt: string | null;
  recentlyAddedUser: AddUserResponseWithData | null;

  // Loading states
  usersLoading: boolean;
  activeUsersLoading: boolean;
  visitStatsLoading: boolean;
  timeSavingsLoading: boolean;
  topSavingsLoading: boolean;
  usageStatsLoading: boolean;

  // Error states
  usersError: string | null;
  activeUsersError: string | null;
  visitStatsError: string | null;
  timeSavingsError: string | null;
  topSavingsError: string | null;
  usageStatsError: string | null;

  // Actions
  fetchUsers: () => Promise<void>;
  fetchActiveUsers: (days?: number) => Promise<void>;
  fetchVisitStats: (days?: number) => Promise<void>;
  fetchTimeSavings: (days?: number) => Promise<void>;
  fetchTopSavingsClinicians: (days?: number) => Promise<void>;
  fetchUsageStats: () => Promise<void>;
  fetchAllOverviewData: () => Promise<void>;
  addNewUser: (userData: AddUserData) => Promise<AddUserResponse>;
  resetPassword: (username: string, newPassword: string) => Promise<void>;
  deactivateUserAccount: (username: string) => Promise<void>;
  clearRecentlyAddedUser: () => void;
}

export const rogerDocSlice: StateCreator<RogerDocSlice> = (set, get) => ({
  // Initial state
  users: [],
  activeUsers: [],
  visitStats: null,
  timeSavings: null,
  topSavingsClinicians: [],
  usageStats: [],
  usageCalculatedAt: null,
  recentlyAddedUser: null,

  usersLoading: false,
  activeUsersLoading: false,
  visitStatsLoading: false,
  timeSavingsLoading: false,
  topSavingsLoading: false,
  usageStatsLoading: false,

  usersError: null,
  activeUsersError: null,
  visitStatsError: null,
  timeSavingsError: null,
  topSavingsError: null,
  usageStatsError: null,

  // Actions
  fetchUsers: async () => {
    set({ usersLoading: true, usersError: null });
    try {
      const users = await getAllUsers();
      set({ users, usersLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch users';
      set({ usersError: message, usersLoading: false });
      throw error;
    }
  },

  fetchActiveUsers: async (days = 30) => {
    set({ activeUsersLoading: true, activeUsersError: null });
    try {
      const activeUsers = await getActiveUsers(days);
      set({ activeUsers, activeUsersLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch active users';
      set({ activeUsersError: message, activeUsersLoading: false });
      throw error;
    }
  },

  fetchVisitStats: async (days = -1) => {
    set({ visitStatsLoading: true, visitStatsError: null });
    try {
      const visitStats = await getVisitStats(days);
      set({ visitStats, visitStatsLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch visit stats';
      set({ visitStatsError: message, visitStatsLoading: false });
      throw error;
    }
  },

  fetchTimeSavings: async (days = -1) => {
    set({ timeSavingsLoading: true, timeSavingsError: null });
    try {
      const timeSavings = await getTimeSavings(days);
      set({ timeSavings, timeSavingsLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch time savings';
      set({ timeSavingsError: message, timeSavingsLoading: false });
      throw error;
    }
  },

  fetchTopSavingsClinicians: async (days = 30) => {
    set({ topSavingsLoading: true, topSavingsError: null });
    try {
      const topSavingsClinicians = await getTopSavingsClinicians(days);
      set({ topSavingsClinicians, topSavingsLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch top savings';
      set({ topSavingsError: message, topSavingsLoading: false });
      throw error;
    }
  },

  fetchUsageStats: async () => {
    set({ usageStatsLoading: true, usageStatsError: null });
    try {
      const response = await getUsageStats();
      set({
        usageStats: response.usage_by_clinician,
        usageCalculatedAt: response.calculated_at,
        usageStatsLoading: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch usage stats';
      set({ usageStatsError: message, usageStatsLoading: false });
      throw error;
    }
  },

  fetchAllOverviewData: async () => {
    // Fetch all overview data in parallel
    await Promise.all([
      get().fetchUsers(),
      get().fetchActiveUsers(30),
      get().fetchVisitStats(-1),
      get().fetchTimeSavings(30),
      get().fetchTopSavingsClinicians(30),
      get().fetchUsageStats(),
    ]);
  },

  addNewUser: async (userData: AddUserData) => {
    const response = await addUser(userData);
    // Combine API response with original user data for display
    const newUserWithData: AddUserResponseWithData = {
      ...response,
      ...userData,
    };
    set({ recentlyAddedUser: newUserWithData });
    // Refresh users list
    await get().fetchUsers();
    return response;
  },

  resetPassword: async (username: string, newPassword: string) => {
    await resetUserPassword(username, newPassword);
    // Optionally refresh users list
    await get().fetchUsers();
  },

  deactivateUserAccount: async (username: string) => {
    await deactivateUser(username);
    // Refresh users list to reflect deactivation
    await get().fetchUsers();
  },

  clearRecentlyAddedUser: () => {
    set({ recentlyAddedUser: null });
  },
});
